import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./cartpage.module.css";
import HomePageHeader from "./../../components/HomePageHeader";
import HomePageFooter from "./../../components/HomePageFooter";
import CartItem from "../../components/CartItem";
import { useTranslation } from "react-i18next";
import { displayCart, removeFromCart } from "../../../src/apis/cart";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCookies } from "react-cookie";
import { formatPrice } from '../../utils/product';

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cookies] = useCookies(["access_token"]);
  const token = cookies.access_token;

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const response = await displayCart(token);
        console.log("Cart items response:", response);

        if (response.data.products && Array.isArray(response.data.products)) {
          const formattedItems = response.data.products.map((item) => {
            const metadata = item.productMetadata;

            return {
              productImage: item.images ? item.images[0].imageURL : "",
              productCode: item.productCode,
              productTitle: item.productName,
              shopName: item.sellerName,
              productLink: `${item.productId}`,
              productAlt: item.description,
              price: item.price,
              quantity: item.quantity,
              totalPrice: item.subtotal,
              selected: false,
              metadata: metadata,
            };
          });

          setCartItems(formattedItems);
        } else {
          console.error("Unexpected response format:", response);
          toast.error(t("failed_to_load_cart_items"), { transition: Bounce });
        }
      } catch (error) {
        console.error("Failed to load cart items", error);
        toast.error(t("failed_to_load_cart_items"), { transition: Bounce });
      }
    };

    fetchCartItems();
  }, [token, t]);

  const handleSelectAll = () => {
    const newCartItems = cartItems.map((item) => ({
      ...item,
      selected: !selectAll,
    }));
    setCartItems(newCartItems);
    setSelectAll(!selectAll);
  };

  const handleSelectItem = (index) => {
    const newCartItems = [...cartItems];
    newCartItems[index].selected = !newCartItems[index].selected;
    setCartItems(newCartItems);
  };

  const handleDecrease = (index) => {
    const newCartItems = [...cartItems];
    if (newCartItems[index].quantity > 1) {
      newCartItems[index].quantity -= 1;
      newCartItems[index].totalPrice =
        newCartItems[index].price * newCartItems[index].quantity;
      setCartItems(newCartItems);
    }
  };

  const handleIncrease = (index) => {
    const newCartItems = [...cartItems];
    if (newCartItems[index].quantity < newCartItems[index].stockQuantity) {
      newCartItems[index].quantity += 1;
      newCartItems[index].totalPrice =
        newCartItems[index].price * newCartItems[index].quantity;
      setCartItems(newCartItems);
    }
  };

  const handleQuantityChange = (index, quantity) => {
    const newCartItems = [...cartItems];
    const newQuantity = parseInt(quantity, 10);

    if (
      !isNaN(newQuantity) &&
      newQuantity > 0 &&
      newQuantity <= newCartItems[index].stockQuantity
    ) {
      newCartItems[index].quantity = newQuantity;
      newCartItems[index].totalPrice = newCartItems[index].price * newQuantity;
      setCartItems(newCartItems);
    }
  };

  const handleDelete = async (index) => {
    try {
      await removeFromCart(cartItems[index].productLink, token);
      const newCartItems = cartItems.filter((_, i) => i !== index);
      setCartItems(newCartItems);
      toast.success(t("item_removed_successfully"), { transition: Bounce });
    } catch (error) {
      console.error("Failed to remove item from cart", error);
      toast.error(t("failed_to_remove_item_from_cart"), { transition: Bounce });
    }
  };

  const handleDeleteSelected = async () => {
    const selectedItems = cartItems.filter((item) => item.selected);
    try {
      for (const item of selectedItems) {
        await removeFromCart(item.productLink, token);
      }
      const newCartItems = cartItems.filter((item) => !item.selected);
      setCartItems(newCartItems);
      setSelectAll(false);
      toast.success(t("selected_items_removed_successfully"), {
        transition: Bounce,
      });
    } catch (error) {
      console.error("Failed to remove selected items from cart", error);
      toast.error(t("failed_to_remove_selected_items_from_cart"), {
        transition: Bounce,
      });
    }
  };

  const handleMoveToWishlist = () => {
    alert("Move selected items to wishlist!");
  };

  const calculateTotalAmount = () => {
    return cartItems.reduce((total, item) => {
      if (item.selected) {
        return total + item.totalPrice;
      }
      return total;
    }, 0);
  };

  const selectedItemCount = cartItems.filter((item) => item.selected).length;

  const handleCheckout = () => {
    navigate("/checkout", {
      state: {
        cartItems: cartItems,
      },
    });
  };

  return (
    <div className={styles.background}>
      <HomePageHeader />
      <h1 className={styles.cart}>{t("your_cart")}</h1>

      <div className="container bg-white">
        <div className="d-flex align-items-start p-3 border-bottom">
          <div className="col-1 d-flex justify-content-center">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              aria-label="Select all products"
            />
          </div>
          <div className="col-3 d-flex align-items-start">{t("products")}</div>
          <div className="col-2"></div>
          <div className="col-1 text-center">{t("price")}</div>
          <div className="col-2 d-flex text-center">{t("quantity")}</div>
          <div className="col-1 text-end align-items-center">{t("total")}</div>
          <div className="col-2 d-flex flex-column align-items-center px-3">
            {t("actions")}
          </div>
        </div>
        {cartItems.length > 0 ? (
          cartItems.map((item, index) => (
            <CartItem
              key={index}
              productImage={item.productImage}
              productTitle={item.productTitle}
              shopName={item.shopName}
              productLink={item.productLink}
              productAlt={item.productAlt}
              price={item.price}
              quantity={item.quantity}
              totalPrice={item.totalPrice}
              selected={item.selected}
              metadata={item.metadata}
              onSelect={() => handleSelectItem(index)}
              onDecrease={() => handleDecrease(index)}
              onIncrease={() => handleIncrease(index)}
              onQuantityChange={(quantity) =>
                handleQuantityChange(index, quantity)
              }
              onDelete={() => handleDelete(index)}
            />
          ))
        ) : (
          <div className={`text-center ${styles.custompadding}`}>
            <h2>{t("there_are_no_products_in_cart")}</h2>
          </div>
        )}
      </div>
      <div className="container bg-white my-20">
        <div className="d-flex align-items-start p-3 border-bottom">
          <div className="col-2">
            {t("selected")}: {selectedItemCount}
          </div>
          <div className="col-1">
            <button onClick={handleDeleteSelected} className={styles.delete}>
              {t("delete")}
            </button>
          </div>
          <div className="col-3">
            <button onClick={handleMoveToWishlist} className={styles.save}>
              {t("save_to_favourited_folder")}
            </button>
          </div>
          <div className="col-3">
            <p>
              {t("total_checkout")}:
              <span className={styles.total}>
                {" "}
                {formatPrice(calculateTotalAmount())}
              </span>
            </p>
          </div>
          <div className="col-3">
            <button onClick={handleCheckout} className={styles.butt}>
              {t("buy_product")}
            </button>
          </div>
        </div>
      </div>
      <HomePageFooter />
      <ToastContainer />
    </div>
  );
};

export default CartPage;
